import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSelectedEmployee } from '../context/SelectedEmployeeContext';
import Icon from './Icons';
import AppModal from './AppModal';
import { getApiMessage } from '../utils/api';
import { createEmployee } from '../services/profileService';
import './EmployeeDropdown.css';

const DROPDOWN_PAGES = ['/profile', '/leaves', '/salary', '/bonus'];

function isLastWorkingDateMoreThanThreeMonthsOld(employee) {
  const rawLastWorkingDate = String(employee?.last_working_date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(rawLastWorkingDate)) return false;

  const lastWorkingDate = new Date(`${rawLastWorkingDate}T00:00:00`);
  if (Number.isNaN(lastWorkingDate.getTime())) return false;

  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setMonth(cutoff.getMonth() - 3);

  return lastWorkingDate < cutoff;
}

function getEmployeeRoleLabel(employee) {
  if (employee?.is_super_admin) return 'Super Admin';
  if (employee?.is_admin) return 'Admin';
  return '';
}

export default function EmployeeDropdown() {
  const { user, isAdmin } = useAuth();
  const { employees, selectedEmployee, setSelectedEmployee, fetchEmployees, loadingEmployees } = useSelectedEmployee();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    };
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // Only show for admins on profile/leave/salary pages
  if (!isAdmin || !DROPDOWN_PAGES.includes(location.pathname)) return null;

  const current = selectedEmployee || user;
  const currentRoleLabel = getEmployeeRoleLabel(current);
  const currentName = current
    ? `${current.first_name || ''} ${current.last_name || ''}`.trim()
    : 'Select Employee';
  const currentInitials = current
    ? `${current.first_name?.[0] || ''}${current.last_name?.[0] || ''}`.toUpperCase()
    : 'U';
  const isSelf = current?.username === user?.username;

  const matchesSearch = (emp) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
    return (
      name.toLowerCase().includes(q) ||
      (emp.designation || '').toLowerCase().includes(q) ||
      (emp.username || '').toLowerCase().includes(q) ||
      (emp.department || '').toLowerCase().includes(q)
    );
  };

  const hasSearch = search.trim().length > 0;
  const filtered = employees
    .filter((emp) => matchesSearch(emp) && (hasSearch || !isLastWorkingDateMoreThanThreeMonthsOld(emp)))
    .sort((a, b) => {
      const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase() || a.username;
      const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase() || b.username;
      return nameA.localeCompare(nameB);
    });

  const handleSelect = (emp) => {
    setSelectedEmployee(emp);
    setOpen(false);
    setSearch('');
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
    setSearch('');
  };

  return (
    <div className="emp-dd" ref={ref}>
      <button className="emp-dd-trigger" onClick={handleToggle} type="button">
        <div className="emp-dd-trigger-avatar">{currentInitials}</div>
        <div className="emp-dd-trigger-info">
          <span className="emp-dd-trigger-name">{currentName}</span>
          {current?.username && <span className="emp-dd-trigger-designation">{current.username}</span>}
        </div>
        {currentRoleLabel && (
          <div className="dropdown-admin-badge admin-badge-generated">
            <span className="">{currentRoleLabel}</span>
          </div>
        )}
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} className="emp-dd-trigger-chevron" />
      </button>

      {open && (
        <div className="emp-dd-panel">
          <div className="emp-dd-search-wrap">
            <div className="emp-dd-search">
              <Icon name="search" size={16} />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search by name, designation, department..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="emp-dd-search-clear" onClick={() => setSearch('')} type="button" aria-label="Clear">
                  <Icon name="close" size={12} />
                </button>
              )}
            </div>
          </div>

          <div className="emp-dd-list">
            <div className="emp-dd-section-label">
              All Employees
              <span className="emp-dd-count">{loadingEmployees ? '...' : filtered.length}</span>
            </div>

            {loadingEmployees ? (
              <div className="emp-dd-empty">
                <div className="spinner" />
                <span>Loading employees...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="emp-dd-empty">
                <Icon name="search" size={24} />
                <span>No employees found</span>
              </div>
            ) : (
              filtered.map((emp) => {
                const empName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                const empInitials = `${emp.first_name?.[0] || ''}${emp.last_name?.[0] || ''}`.toUpperCase();
                const isMe = emp.username === user?.username;
                const isActive = isMe ? isSelf : selectedEmployee?.username === emp.username;

                return (
                  <button
                    key={emp.username}
                    className={`emp-dd-item${isActive ? ' active' : ''}`}
                    onClick={() => handleSelect(emp)}
                    type="button"
                  >
                    <div className="emp-dd-item-avatar">{empInitials}</div>
                    <div className="emp-dd-item-info">
                      <span className="emp-dd-item-name">
                        {empName || emp.username}{isMe ? ' (You)' : ''}
                      </span>
                      <span className="emp-dd-item-meta">
                        {emp.username}
                      </span>
                    </div>
                    {getEmployeeRoleLabel(emp) ? (
                      <div className="dropdown-admin-badge admin-badge-generated">
                        <span className="">{getEmployeeRoleLabel(emp)}</span>
                      </div>
                    ) : ""}
                    {isActive && <Icon name="check" size={16} className="emp-dd-item-check" />}
                  </button>
                );
              })
            )}
          </div>

          {/* Add Employee */}
          <div className="emp-dd-add-divider" />
          <button
            className="emp-dd-add-btn"
            type="button"
            onClick={() => { setOpen(false); setShowAddModal(true); }}
          >
            <span className="emp-dd-add-btn-icon"><Icon name="plus" size={14} /></span>
            <span>Add New Employee</span>
          </button>
        </div>
      )}

      {showAddModal && (
        <AddEmployeeModal
          onClose={() => setShowAddModal(false)}
          onCreated={async (newEmp) => {
            await fetchEmployees();
            setSelectedEmployee({
              username: newEmp.username,
              first_name: newEmp.first_name,
              last_name: newEmp.last_name,
            });
            const empName = `${newEmp.first_name} ${newEmp.last_name}`.trim();
            // Store in sessionStorage so it survives navigation
            sessionStorage.setItem('emp_add_success', `${empName} has been added successfully.`);
            if (location.pathname !== '/profile') {
              navigate('/profile');
            } else {
              // Already on profile — trigger toast manually
              window.dispatchEvent(new Event('emp-added'));
            }
          }}
        />
      )}
    </div>
  );
}

function AddEmployeeModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', username: '', password: '' });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [usernameApplied, setUsernameApplied] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => firstInputRef.current?.focus(), 50);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const generateUsername = (firstName, lastName) => {
    const f = firstName.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    const l = lastName.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!f || !l) return '';
    return `${f}.${l}`;
  };

  const suggestedUsername = generateUsername(form.first_name, form.last_name);
  const showSuggestion = suggestedUsername && form.username !== suggestedUsername;

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'First name is required.';
    if (!form.last_name.trim()) errs.last_name = 'Last name is required.';
    if (!form.username.trim()) errs.username = 'Username is required.';
    else if (!/^[a-z0-9._-]+$/i.test(form.username.trim())) errs.username = 'Only letters, numbers, dots, hyphens allowed.';

    const pwd = form.password;
    if (!pwd) errs.password = 'Password is required.';
    else if (pwd.length < 10) errs.password = 'Must be at least 10 characters.';
    else if (!/[A-Z]/.test(pwd)) errs.password = 'Must include an uppercase letter.';
    else if (!/[a-z]/.test(pwd)) errs.password = 'Must include a lowercase letter.';
    else if (!/[0-9]/.test(pwd)) errs.password = 'Must include a number.';
    else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) errs.password = 'Must include a special character.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    try {
      const newEmp = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        username: form.username.trim().toLowerCase(),
      };
      const createdEmployee = await createEmployee({ ...newEmp, password: form.password });
      onClose();
      await onCreated(createdEmployee || newEmp);
    } catch (err) {
      const status = err.response?.status;
      const msg = getApiMessage(err, 'Failed to create employee.');
      if (status === 409) {
        setErrors((e) => ({ ...e, username: `"${form.username.trim().toLowerCase()}" is already taken. Please choose a different username.` }));
      } else {
        setApiError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal title="Add New Employee" onClose={onClose} contentClassName="add-emp-modal">
        <form onSubmit={handleSubmit} noValidate>
          {apiError && (
            <div className="alert alert-error" style={{ marginBottom: 'var(--space-4)' }}>
              {apiError}
            </div>
          )}
          <div className="add-emp-form-body">
            <div className="add-emp-row">
              <div className="form-group">
                <label className="form-label">First Name <span className="add-emp-required">*</span></label>
                <input
                  ref={firstInputRef}
                  type="text"
                  className={`form-input${errors.first_name ? ' error' : ''}`}
                  value={form.first_name}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  placeholder="e.g. Rohit"
                />
                {errors.first_name && <p className="form-error">{errors.first_name}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Last Name <span className="add-emp-required">*</span></label>
                <input
                  type="text"
                  className={`form-input${errors.last_name ? ' error' : ''}`}
                  value={form.last_name}
                  onChange={(e) => updateField('last_name', e.target.value)}
                  placeholder="e.g. Roi"
                />
                {errors.last_name && <p className="form-error">{errors.last_name}</p>}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Username <span className="add-emp-required">*</span></label>
              <input
                type="text"
                className={`form-input${errors.username ? ' error' : ''}`}
                value={form.username}
                onChange={(e) => updateField('username', e.target.value)}
                placeholder="e.g. rohit.roi"
                autoComplete="off"
              />
              {errors.username && <p className="form-error">{errors.username}</p>}
              {usernameApplied && (
                <p className="add-emp-username-applied">
                  <Icon name="check" size={12} /> Username set to <strong>{form.username}</strong>
                </p>
              )}
              {showSuggestion && !usernameApplied && (
                <p className="add-emp-username-suggestion">
                  Suggested: <button type="button" onClick={() => {
                    updateField('username', suggestedUsername);
                    setUsernameApplied(true);
                    setTimeout(() => setUsernameApplied(false), 2500);
                  }}>{suggestedUsername}</button>
                </p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Password <span className="add-emp-required">*</span></label>
              <div className="add-emp-password-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`form-input${errors.password ? ' error' : ''}`}
                  value={form.password}
                  onChange={(e) => updateField('password', e.target.value)}
                  placeholder="Min 10 characters"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="add-emp-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'eyeOff' : 'eye'} size={16} />
                </button>
              </div>
              {errors.password && <p className="form-error">{errors.password}</p>}
              <p className="add-emp-pwd-hint">Min 10 chars with uppercase, lowercase, number &amp; special character.</p>
            </div>
          </div>

          <div className="modal-actions app-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Employee'}
            </button>
          </div>
        </form>
    </AppModal>
  );
}
